<template>
  <m-error
    v-if="otherEmail != null"
    :email="otherEmail"
    @exit="goHome"
  />
  <div
    v-else-if="!signedRequest"
    class="msteams-connect"
  >
    <m-card class="_card">
      <div class="_hero">
        <h2 class="_title">
          {{ $t('msTeamsConnect.title') }}
        </h2>
        <p class="_body">
          {{ $t('msTeamsConnect.missingActivity') }}
        </p>
      </div>
      <div class="_actions">
        <m-btn
          block
          color="primary"
          class="_action-btn"
          @click="goHome"
        >
          {{ $t('msTeamsConnect.cancel') }}
        </m-btn>
      </div>
    </m-card>
  </div>
  <integration-connect-card
    v-else
    logo-icon="ms-teams"
    :title="$t('msTeamsConnect.title')"
    :body="$t('msTeamsConnect.body')"
    :permissions-heading="$t('msTeamsConnect.permissionsHeading')"
    :permissions="permissions"
    :allow-label="$t('msTeamsConnect.allow')"
    :cancel-label="$t('msTeamsConnect.cancel')"
    :loading="loading"
    @allow="allowAccess"
    @cancel="goHome"
  />
</template>

<script setup>
import IntegrationConnectCard from '@/components/integrations/IntegrationConnectCard.vue';
import MError from '@/components/msteams/MSError.vue';
import useAccountSwitch from '@/composables/account-switch';
import useSnackbar from '@/composables/snackbar';
import { computed, ref } from 'vue';
import { doConnectBot } from '@/api';
import { msTeamsConnectError, msTeamsConnectQuery } from '@/lib/constants';
import { routeName } from 'shared/constants.json';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const snackbar = useSnackbar();
const { switchAccount } = useAccountSwitch();

const signedRequest = computed(() => route.query.s);

const permissions = [
  { icon: 'eye', text: t('msTeamsConnect.permissions.readGoals') },
  { icon: 'goals', text: t('msTeamsConnect.permissions.updateGoals') },
  { icon: 'bell', text: t('msTeamsConnect.permissions.notifications') },
];

const loading = ref(false);
const otherEmail = ref(null);

const accountSlug = (account) => account?.activeSlug?.slug;

const notificationsTarget = (account, query) => {
  const slug = accountSlug(account);
  return slug
    ? { name: routeName.myNotifications, params: { tenantSlug: slug }, query }
    : { name: routeName.myNotifications, query };
};

const allowAccess = (account) => {
  if (!signedRequest.value) {
    return;
  }
  loading.value = true;
  doConnectBot(
    signedRequest.value,
    account.uid,
  ).then((response) => {
    if (response.status === 200) {
      const target = notificationsTarget(account, { [msTeamsConnectQuery.success]: '1' });
      switchAccount({ uid: account.uid, isCurrent: false }, target);
      return;
    }
    loading.value = false;
    if (response.status === 400) {
      otherEmail.value = response.data;
      return;
    }
    if (response.status === 403) {
      const target = notificationsTarget(account, { [msTeamsConnectQuery.error]: msTeamsConnectError.notEnabled });
      switchAccount({ uid: account.uid, isCurrent: false }, target);
      return;
    }
    snackbar.error(t('msTeams.connectionError'));
  }).catch(() => {
    loading.value = false;
    snackbar.error(t('msTeams.connectionError'));
  });
};

const goHome = () => {
  router.replace('/');
};
</script>

<style scoped lang="scss" type="text/scss">
  .msteams-connect {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 2.4rem;
    background-color: $side-nav-background-color;

    ._card {
      width: 100%;
      max-width: 52rem;
    }

    ._hero {
      padding: 3.6rem 2rem 2.4rem;
      text-align: center;

      ._title {
        margin: 0 0 1.2rem;
        font-size: $font-size-8;
        font-weight: $font-weight-bold;
        color: $font-color-primary;
      }

      ._body {
        margin: 0;
        color: $font-color-secondary;
        font-size: $font-size-5;
        line-height: 1.55;
      }
    }

    ._actions {
      padding-top: 1.6rem;

      ._action-btn {
        width: 100%;
      }
    }
  }
</style>
